import React from 'react';
import { Button, Select, TextField, TextStyle } from '@shopify/polaris';
import './TitleRules.css';

export type TitleOperator = 'equals' | 'contains' | 'starts_with' | 'ends_with';
export interface TitleRule {
  operator: TitleOperator;
  value: string;
}

const operators = [
  { label: 'Equals', value: 'equals' },
  { label: 'Contains', value: 'contains' },
  { label: 'Starts with', value: 'starts_with' },
  { label: 'Ends with', value: 'ends_with' },
];

export const titleRuleError = (rule: TitleRule): string | undefined => {
  if (!operators.some(option => option.value === rule.operator)) return 'Choose a title condition.';
  if (!rule.value.trim()) return 'Enter title text or remove this condition.';
  if (Array.from(rule.value.trim()).length > 200) return 'Use 200 characters or fewer.';
  return undefined;
};

export const invalidTitleRules = (rules: TitleRule[]): boolean =>
  rules.length > 100 || rules.some(rule => !!titleRuleError(rule));

export const serializeTitleRules = (rules: TitleRule[]): TitleRule[] => {
  if (invalidTitleRules(rules)) throw new Error('Invalid title conditions');
  return rules.map(rule => ({ operator: rule.operator, value: rule.value.trim() }));
};

interface TitleRulesProps {
  label: 'Include title' | 'Exclude title' | 'Filter title';
  title: string;
  match: 'ANY' | 'ALL';
  rules: TitleRule[];
  onChange: (rules: TitleRule[]) => void;
}

export default function TitleRules({ label, title, match, rules, onChange }: TitleRulesProps) {
  const update = (index: number, change: Partial<TitleRule>) =>
    onChange(rules.map((rule, current) => current === index ? { ...rule, ...change } : rule));
  return (
    <div className="title-rules" role="group" aria-label={label + ' conditions'}>
      <TextStyle variation="strong">{title}</TextStyle>
      <p>Match <strong>{match}</strong> of these product title conditions. Matching ignores letter case. Commas are treated as part of the title text.</p>
      {rules.map((rule, index) => (
        <div className="title-rule-row" key={index}>
          <Select label={`${label} operator ${index + 1}`} labelHidden
            options={operators} value={rule.operator}
            onChange={operator => update(index, { operator: operator as TitleOperator })} />
          <TextField label={`${label} text ${index + 1}`} labelHidden
            placeholder="Enter title text" value={rule.value}
            onChange={value => update(index, { value })} error={titleRuleError(rule)} />
          <Button plain destructive accessibilityLabel={`Remove ${label.toLowerCase()} condition ${index + 1}`}
            onClick={() => onChange(rules.filter((_, current) => current !== index))}>Remove</Button>
        </div>
      ))}
      {rules.length >= 100 && <p role="status">You can add up to 100 title conditions.</p>}
      <Button plain disabled={rules.length >= 100} accessibilityLabel={`Add ${label.toLowerCase()} condition`}
        onClick={() => onChange([...rules, { operator: 'contains', value: '' }])}>Add condition</Button>
    </div>
  );
}
